<template>
  <Teleport to="body">
    <dialog ref="dialog" class="writing-abstract" :class="{ 'has-motion': animate }" aria-labelledby="paper-abstract-title" @cancel.prevent="close" @keydown.stop>
      <header class="abstract-header">
        <div>
          <span class="abstract-label">Abstract</span>
          <h2 id="paper-abstract-title">Early Epistemic Settlement in AI-Assisted Writing</h2>
          <p class="abstract-author">Han-yu Wang</p>
        </div>
        <button ref="closeButton" type="button" class="abstract-close" aria-label="Close abstract" @click="close">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg>
        </button>
      </header>
      <div class="abstract-body" tabindex="0" aria-label="Paper abstract">
        <p>In trying to complete a passage, an author can make connections among her materials that change what she can argue and the demands the argument must meet. A language model can supply a passage that does the work required at that point in her argument. Accepting it can end her own attempts before those connections have developed. I call this interruption early epistemic settlement.</p>
        <p>It can occur even when she fully understands the response and correctly judges it adequate for the passage's present role in the argument. The answer can satisfy the desire for resolution that kept her at work. Returning to her unfinished attempt would take more effort, and she may be unable to anticipate what she could achieve by continuing it. With repeated assistance, accepted answers shape what the writer asks next and which relations she goes on to develop.</p>
        <p>Useful answers can thus sustain inquiry while cutting short the work through which an author could form arguments that accommodate demands she has yet to recognize.</p>
      </div>
      <footer class="abstract-footer">
        <a class="resource-action" href="https://arxiv.org/abs/2609.09332" target="_blank" rel="noopener"><span>Read the paper</span><ArrowUpRightIcon /></a>
      </footer>
    </dialog>
  </Teleport>
</template>

<script>
import { lockBodyScroll } from '../utils/bodyScrollLock';
import { initDialogBackdrop } from '../utils/dialogBackdrop';
import '../assets/css/resource-action.css';

export default {
  name: 'WritingAbstractDialog',
  props: { animate: Boolean },
  emits: ['close'],
  mounted() {
    this._trigger = document.activeElement;
    this._releaseScroll = lockBodyScroll();
    this.$refs.dialog.showModal();
    this._stopBackdrop = initDialogBackdrop(this.$refs.dialog, this.close);
    this.$refs.closeButton.focus({ preventScroll: true });
  },
  beforeUnmount() {
    this._stopBackdrop?.();
    this.$refs.dialog?.close();
    this._releaseScroll?.();
    if (this._trigger?.isConnected) this._trigger.focus({ preventScroll: true });
  },
  methods: { close() { this.$emit('close'); } },
};
</script>

<style scoped>
.writing-abstract {
  --abstract-paper: #fcfbf8;
  --abstract-ink: #353430;
  --abstract-muted: #73716c;
  box-sizing: border-box;
  width: min(720px, calc(100vw - 40px));
  max-height: calc(100dvh - 48px);
  margin: auto;
  padding: 0;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--abstract-ink) 13%, transparent);
  border-radius: 6px;
  background: var(--abstract-paper);
  color: var(--abstract-ink);
  box-shadow: 0 24px 80px #0002;
  overscroll-behavior: contain;
}
.writing-abstract[open] { display: flex; flex-direction: column; }
.writing-abstract::backdrop { background: rgb(25 24 23 / 28%); backdrop-filter: blur(6px); }
.writing-abstract.has-motion[open] { transition: opacity 180ms ease, transform 180ms cubic-bezier(.23, 1, .32, 1); }
@starting-style { .writing-abstract.has-motion[open] { opacity: 0; transform: translateY(6px) scale(.99); } }
.abstract-header { display: flex; flex: none; align-items: flex-start; gap: 20px; padding: 32px 36px 24px; }
.abstract-header > div { min-width: 0; }
.abstract-label { display: block; color: var(--abstract-muted); font: 11px/1.5 'Quattrocento Sans', sans-serif; letter-spacing: .1em; text-transform: uppercase; }
.abstract-header h2 { margin: 12px 0 10px; font: 400 38px/1.08 'EB Garamond', Georgia, serif; letter-spacing: -.025em; text-wrap: balance; }
.abstract-author { margin: 0; color: var(--abstract-muted); font: 13px/1.5 'Quattrocento Sans', sans-serif; }
.abstract-close { display: grid; place-items: center; flex: 0 0 40px; width: 40px; height: 40px; margin: -10px -10px 0 0; padding: 10px; border: 0; border-radius: 50%; background: transparent; color: inherit; cursor: var(--site-interactive-cursor); transition: background-color 140ms ease, transform 140ms ease; }
.abstract-close svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.3; stroke-linecap: round; }
.abstract-close:active { transform: scale(.97); }
.abstract-body { min-height: 0; padding: 0 36px; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin; }
.abstract-body p { margin: 0 0 20px; color: inherit; font: 15px/1.85 'PT Serif', Georgia, serif; }
.abstract-body p:last-child { margin-bottom: 0; }
.abstract-footer { flex: none; padding: 18px 36px 24px; }
.abstract-footer .resource-action { color: var(--abstract-ink); font-weight: 400; font-size: 14px; }
.abstract-footer svg { width: 14px; height: 14px; }
.abstract-close:focus-visible, .abstract-body:focus-visible { outline: 1px solid var(--site-accent, #505050); outline-offset: -3px; }
:global(body.dark-mode .writing-abstract) { --abstract-paper: #262523; --abstract-ink: #e7e3dc; --abstract-muted: #b6b1a8; color-scheme: dark; box-shadow: 0 24px 80px #0005; }
@media (hover: hover) and (pointer: fine) { .abstract-close:hover { background: color-mix(in srgb, currentColor 7%, transparent); } }
@media (max-width: 600px) {
  .writing-abstract { width: calc(100vw - 28px); max-height: calc(100dvh - 32px); }
  .abstract-header { gap: 12px; padding: 26px 24px 22px; }
  .abstract-header h2 { font-size: 31px; }
  .abstract-body { padding: 0 24px; }
  .abstract-body p { font-size: 14px; line-height: 1.85; }
  .abstract-footer { padding: 16px 24px 20px; }
  .abstract-close { flex-basis: 44px; width: 44px; height: 44px; }
}
@media (prefers-reduced-motion: reduce) {
  .writing-abstract.has-motion[open], .abstract-close { transition: none; }
  @starting-style { .writing-abstract.has-motion[open] { opacity: 1; transform: none; } }
}
</style>
